import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { API_URL } from './constants';

// Constants for storage keys
const ACCESS_TOKEN_KEY = 'auth_access_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';

// Check if running in Capacitor native platform
export const isNative = Capacitor.isNativePlatform();

// Token management functions for native apps
export const saveTokens = async (accessToken: string, refreshToken: string): Promise<void> => {
  if (!isNative) return;

  await Preferences.set({ key: ACCESS_TOKEN_KEY, value: accessToken });
  await Preferences.set({ key: REFRESH_TOKEN_KEY, value: refreshToken });
};

export const getAccessToken = async (): Promise<string | null> => {
  if (!isNative) return null;

  const { value } = await Preferences.get({ key: ACCESS_TOKEN_KEY });
  return value;
};

export const getRefreshToken = async (): Promise<string | null> => {
  if (!isNative) return null;

  const { value } = await Preferences.get({ key: REFRESH_TOKEN_KEY });
  return value;
};

export const clearTokens = async (): Promise<void> => {
  if (!isNative) return;

  await Preferences.remove({ key: ACCESS_TOKEN_KEY });
  await Preferences.remove({ key: REFRESH_TOKEN_KEY });
};

// Mutex to prevent concurrent token refresh calls
let refreshPromise: Promise<RefreshResult> | null = null;

// Refresh token function for native apps
export const refreshAccessToken = async (): Promise<RefreshResult> => {
  // If a refresh is already in progress, wait for it instead of starting a new one
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = _doRefreshAccessToken();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

/**
 * Why a refresh did not produce a new token.
 *
 * "failed" and "throttled" have to stay apart. Both mean no new token right
 * now, but only the first means the session is over. Collapsing them into one
 * boolean is what made a rate limit log people out of the app: the refresh
 * came back 429, the caller read that as "your session is gone" and threw the
 * stored tokens away, taking a refresh token with up to seven days left on it.
 */
export type RefreshOutcome = "ok" | "failed" | "throttled";

export interface RefreshResult {
  outcome: RefreshOutcome;
  /** Seconds the server asked us to wait, when it said so. */
  retryAfter: number | null;
}

const _doRefreshAccessToken = async (): Promise<RefreshResult> => {
  try {
    const refreshToken = await getRefreshToken();

    if (!refreshToken) {
      return { outcome: "failed", retryAfter: null };
    }

    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-native-app': 'true'
      },
      body: JSON.stringify({ refreshToken })
    });

    // Too many requests, or the server is having a moment. Neither says
    // anything about whether this session is still good.
    if (response.status === 429 || response.status >= 500) {
      const kopf = Number(response.headers.get("Retry-After"));
      return {
        outcome: "throttled",
        retryAfter: Number.isFinite(kopf) && kopf > 0 ? kopf : null,
      };
    }

    if (!response.ok) {
      // Anything else here really is a dead refresh token.
      return { outcome: "failed", retryAfter: null };
    }

    const data = await response.json();
    
    // Handle both response formats - direct tokens or nested in data object
    const accessToken = data.accessToken || data.tokens?.accessToken;
    const newRefreshToken = data.refreshToken || data.tokens?.refreshToken;
    
    if (accessToken && newRefreshToken) {
      await saveTokens(accessToken, newRefreshToken);
      return { outcome: "ok", retryAfter: null };
    }

    return { outcome: "failed", retryAfter: null };
  } catch (error) {
    // A thrown fetch is the network, not the session. Signing somebody out
    // because their train went into a tunnel is the wrong answer.
    console.error('Error refreshing token:', error);
    return { outcome: "throttled", retryAfter: null };
  }
};

// Helper to add auth info to fetch requests
export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  // Start with the provided options or default to empty object
  const fetchOptions: RequestInit = { ...options };
  
  // Initialize headers if not present
  fetchOptions.headers = fetchOptions.headers || {};
  
  // For native apps, use token-based auth
  if (isNative) {
    let accessToken = await getAccessToken();
    
    // Add headers
    fetchOptions.headers = {
      ...fetchOptions.headers,
      'x-native-app': 'true',
      ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
    };

    // Make the request
    let response = await fetch(url, fetchOptions);
    
    // If unauthorized and we have a refresh token, try to refresh and retry
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();

      /**
       * The refresh was refused for a reason that has nothing to do with this
       * session. Handing the original 401 back would be a lie: every caller
       * reads a 401 as "signed out". The refusal itself is passed on instead,
       * so the status says what actually happened and carries Retry-After
       * with it.
       */
      if (refreshed.outcome === "throttled") {
        const kopf: Record<string, string> = { "Content-Type": "application/json" };
        if (refreshed.retryAfter) kopf["Retry-After"] = String(refreshed.retryAfter);
        return new Response(JSON.stringify({ error: "rate_limited" }), {
          status: 429,
          headers: kopf,
        });
      }

      if (refreshed.outcome === "ok") {
        // Get the new access token
        accessToken = await getAccessToken();
        
        // Update the Authorization header with the new token
        const headers = { ...fetchOptions.headers } as Record<string, string>;
        headers['Authorization'] = `Bearer ${accessToken}`;
        
        // Retry the request with the new token
        return fetch(url, { ...fetchOptions, headers });
      }
    }
    
    return response;
  } 
  
  // For browser/PWA, use cookie-based auth
  return fetch(url, {
    ...fetchOptions,
    credentials: 'include',
  });
};

/**
 * Safely parse JSON from a Response, returning null if parsing fails.
 * Use this instead of raw response.json() to avoid unhandled crashes
 * from corrupted or non-JSON responses.
 */
export const safeJsonParse = async <T = any>(response: Response): Promise<T | null> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};